import React, { createContext, useState, useContext, ReactNode } from "react";

type ModalOption =
  | "login"
  | "register"
  | "guest"
  | "customer"
  | "recipient"
  | "forgot-password"
  | "reset-password"
  | null;

type LayoutContextProps = {
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: (state: boolean) => void;
  showCart: boolean;
  setShowCart: (state: boolean) => void;
  showOptionModal: boolean;
  setShowOptionModal: (state: boolean) => void;
  showMenu: boolean;
  setShowMenu: (state: boolean) => void;
  toggleMenu: () => void;
  toggleSidebar: () => void;
  toggleCart: () => void;
  activeModal: ModalOption;
  setActiveModal: (option: ModalOption) => void;
};

const LayoutContext = createContext({} as LayoutContextProps);

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error("useLayout must be used within an LayoutProvider");
  }
  return context;
};

export const LayoutProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);
  const [showCart, setShowCart] = useState<boolean>(false);
  const [showOptionModal, setShowOptionModal] = useState(false);
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [activeModal, setActiveModal] = useState<ModalOption>(null);

  const toggleMenu = () => setShowMenu((prev) => !prev);
  const toggleSidebar = () => setIsSidebarCollapsed((prev) => !prev);
  const toggleCart = () => setShowCart((prev) => !prev);

  return (
    <LayoutContext.Provider
      value={{
        isSidebarCollapsed,
        setIsSidebarCollapsed,
        showCart,
        setShowCart,
        showOptionModal,
        setShowOptionModal,
        showMenu,
        setShowMenu,
        toggleMenu,
        toggleSidebar,
        toggleCart,
        activeModal,
        setActiveModal,
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
};
